import { useState } from 'react'
import { Box, Flex, Input, Text, Image, Button } from '@chakra-ui/react'
import { motion } from 'framer-motion'

import { H2 } from '@/components/ui/Headings'
import { useAnalyzeBrand, useGetBrandAnalysis } from '@/api/brand-analysis'
import { useBrandContext } from '@/context/brand-context'
import { useLogoBgColor } from '@/hooks/use-logo-bg-color'
import '@/components/ambient/animations.css'
import { Tooltip } from '@/components/ui/Tooltip'
import AnalysisAnimation from './analysis-animation'

import type { BrandAnalysisResponse } from '@/context/brand-context'

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
}

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' as const } }
}

const glassCard = {
  bg: 'rgba(18, 18, 26, 0.6)',
  backdropFilter: 'blur(12px)',
  borderRadius: '14px',
  border: '1px solid',
  borderColor: 'rgba(255, 255, 255, 0.08)',
  transition: 'all 0.3s ease',
  _hover: {
    borderColor: 'rgba(0, 112, 243, 0.25)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)'
  }
}

function UsageItem({ icon, text }: { icon: string; text: string }) {
  return (
    <Flex alignItems="center" gap="10px">
      <Text fontSize="16px" flexShrink={0}>{icon}</Text>
      <Text color="text.tertiary" textStyle="bodySuperSmall">{text}</Text>
    </Flex>
  )
}

interface BrandAnalysisStepProps {
  onContinue: () => void
}

export default function BrandAnalysisStep({ onContinue }: BrandAnalysisStepProps) {
  const { brandAnalysis, setBrandAnalysis } = useBrandContext()
  const { data: savedAnalysis } = useGetBrandAnalysis()

  const [url, setUrl] = useState('')
  const [phase, setPhase] = useState<'input' | 'loading' | 'results'>(() =>
    brandAnalysis ? 'results' : 'input'
  )
  const [result, setResult] = useState<BrandAnalysisResponse | null>(brandAnalysis)
  const [error, setError] = useState('')

  const { analyzeBrandAsync } = useAnalyzeBrand()

  const effectiveResult = result ?? brandAnalysis ?? (savedAnalysis?.brand ? savedAnalysis : null)
  const effectivePhase = phase === 'loading' ? 'loading' : effectiveResult ? 'results' : phase
  const logoBgColor = useLogoBgColor(effectiveResult?.brand.logo ?? null)

  const normalizeUrl = (input: string): string => {
    const trimmed = input.trim()
    if (/^https?:\/\//i.test(trimmed)) return trimmed
    return `https://${trimmed}`
  }

  const handleAnalyze = async () => {
    if (!url) return
    setError('')
    setPhase('loading')

    try {
      const data = await analyzeBrandAsync(normalizeUrl(url))
      setResult(data)
      setBrandAnalysis(data)
      setPhase('results')
    } catch {
      setError('Could not analyze this URL. Please check and try again.')
      setPhase('input')
    }
  }

  if (effectivePhase === 'loading') {
    return <AnalysisAnimation />
  }

  if (effectivePhase === 'results' && effectiveResult) {
    return (
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '980px',
          margin: '0 auto',
          padding: '0 24px'
        }}
      >
        {/* Brand header row */}
        <motion.div variants={fadeInUp}>
          <Flex alignItems="center" gap="14px" mb="14px">
            {effectiveResult.brand.logo && (
              <Image
                src={effectiveResult.brand.logo}
                alt={effectiveResult.brand.name}
                w="44px"
                h="44px"
                borderRadius="10px"
                objectFit="contain"
                bg={logoBgColor ?? 'bg.surface'}
                border="1px solid"
                borderColor="border.subtle"
              />
            )}
            <Box>
              <H2 color="text.primary" fontWeight={300}>
                {effectiveResult.brand.name}
              </H2>
              <Text color="text.secondary" textStyle="bodySuperSmall" mt="2px">
                {effectiveResult.brand.summary}
              </Text>
            </Box>
          </Flex>
        </motion.div>

        <motion.div variants={fadeInUp}>
          <Box {...glassCard} px="20px" py="14px" w="100%">
            <Text color="white" textStyle="label" mb="12px">
              Design Brief
            </Text>

            {effectiveResult.brand.designDirective && (
              <Box
                mb="12px"
                pl="12px"
                borderLeft="2px solid"
                borderColor="accent.500"
              >
                <Text color="text.primary" textStyle="bodySuperSmall">
                  {effectiveResult.brand.designDirective}
                </Text>
              </Box>
            )}

            {effectiveResult.brand.keywords.length > 0 && (
              <Box>
                <Text color="text.tertiary" textStyle="bodySuperSmall" mb="6px">
                  Design Themes
                </Text>
                <Flex gap="6px" flexWrap="wrap">
                  {effectiveResult.brand.keywords.slice(0, 4).map((kw, i) => (
                    <Text
                      key={i}
                      color="white"
                      textStyle="bodySuperSmall"
                      bg="bg.surface"
                      px="10px"
                      py="4px"
                      borderRadius="6px"
                    >
                      {kw}
                    </Text>
                  ))}
                </Flex>
              </Box>
            )}
          </Box>
        </motion.div>

        <Flex mt="14px" gap="16px" w="100%" direction={{ base: 'column', md: 'row' }}>
          <motion.div variants={fadeInUp} style={{ flex: 1 }}>
            <Box {...glassCard} px="20px" py="14px" h="100%">
              <Text color="white" textStyle="label" mb="12px">
                Brand Colors
              </Text>
              <Flex gap="8px" flexWrap="wrap" mb="12px">
                {effectiveResult.brand.colors.slice(0, 8).map((color, i) => (
                  <Tooltip key={i} content={`${color.name} - ${color.usage}`} showArrow>
                    <Box
                      w="36px"
                      h="36px"
                      borderRadius="8px"
                      bg={color.hex}
                      border="1px solid"
                      borderColor="border.subtle"
                      boxShadow={`0 0 12px ${color.hex}33`}
                      cursor="default"
                      transition="transform 0.2s ease"
                      _hover={{ transform: 'scale(1.15)' }}
                    />
                  </Tooltip>
                ))}
              </Flex>

              <Flex gap="16px" flexWrap="wrap">
                <Box>
                  <Text color="text.tertiary" textStyle="bodySuperSmall" mb="3px">
                    Visual Style
                  </Text>
                  <Text color="accent.400" textStyle="bodySuperSmall" fontWeight={500}>
                    {effectiveResult.brand.visualStyle}
                  </Text>
                </Box>
                {effectiveResult.brand.fonts.length > 0 && (
                  <Box>
                    <Text color="text.tertiary" textStyle="bodySuperSmall" mb="3px">
                      Typography
                    </Text>
                    <Text color="text.primary" textStyle="bodySuperSmall">
                      {effectiveResult.brand.fonts.slice(0, 3).join(', ')}
                    </Text>
                  </Box>
                )}
              </Flex>
            </Box>
          </motion.div>

          <motion.div variants={fadeInUp} style={{ flex: 1 }}>
            <Box {...glassCard} h="100%" px="20px" py="14px">
              <Text color="white" textStyle="label" mb="12px">
                How we use this data
              </Text>
              <Flex gap="16px" flexDirection="column">
                <UsageItem icon="&#127912;" text="Colors guide backgrounds & accents" />
                <UsageItem icon="&#10024;" text="Visual style sets the mood & composition" />
                <UsageItem icon="&#128221;" text="Design brief constrains the AI generator" />
              </Flex>
            </Box>
          </motion.div>
        </Flex>

        <Box h="88px" />

        <Box
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          zIndex={10}
          px="24px"
          pb={{ base: '16px', md: '20px' }}
          pt="12px"
          bg="linear-gradient(to top, rgba(10, 10, 15, 1) 60%, rgba(10, 10, 15, 0))"
        >
          <Flex gap="12px" justifyContent="center" w="100%" maxW="420px" mx="auto">
            <Button
              h="48px"
              minW="132px"
              textStyle="body"
              fontWeight={500}
              borderRadius="10px"
              bg="bg.surface"
              color="text.primary"
              border="1px solid"
              borderColor="border.subtle"
              _hover={{ bg: 'rgba(255, 255, 255, 0.06)', borderColor: 'border.strong' }}
              transition="all 0.3s ease"
              onClick={() => setPhase('input')}
            >
              Go Back
            </Button>
            <Button
              h="48px"
              flex="1"
              textStyle="body"
              fontWeight={500}
              borderRadius="10px"
              bg="accent.500"
              color="white"
              border="1px solid"
              borderColor="accent.500"
              className="animate-glow-pulse"
              _hover={{
                bg: 'accent.400',
                transform: 'translateY(-1px)',
                boxShadow: '0 0 30px rgba(0, 112, 243, 0.3)'
              }}
              transition="all 0.3s ease"
              onClick={() => {
                if (effectiveResult && !brandAnalysis) {
                  setBrandAnalysis(effectiveResult)
                }
                onContinue()
              }}
            >
              Continue
            </Button>
          </Flex>
        </Box>
      </motion.div>
    )
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '560px',
        margin: '0 auto',
        padding: '0 24px'
      }}
    >
      <motion.div variants={fadeInUp}>
        <H2 color="text.primary" textAlign="center" fontWeight={300} mb="12px">
          Let's understand your brand
        </H2>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Text
          color="white"
          textAlign="center"
          textStyle="body"
          mb="28px"
        >
          Enter your website URL and we'll analyze your visual identity
        </Text>
      </motion.div>

      <motion.div variants={fadeInUp} style={{ width: '100%' }}>
        <Box
          bg="rgba(18, 18, 26, 0.5)"
          backdropFilter="blur(16px)"
          borderRadius="20px"
          border="1px solid"
          borderColor="border.subtle"
          p={{ base: '24px', md: '28px' }}
        >
          <Input
            type="text"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value)
              if (error) setError('')
            }}
            placeholder="https://www.example.com"
            h="56px"
            bg="rgba(0, 0, 0, 0.3)"
            border="1px solid"
            borderColor="whiteAlpha.100"
            borderRadius="12px"
            color="text.primary"
            fontSize="16px"
            px="20px"
            mb="16px"
            _placeholder={{ color: 'text.tertiary' }}
            _focus={{
              borderColor: 'accent.500',
              boxShadow:
                '0 0 0 1px rgba(0, 112, 243, 0.5), 0 0 20px rgba(0, 112, 243, 0.1)',
              outline: 'none'
            }}
            _hover={{ borderColor: 'border.strong' }}
            transition="all 0.3s ease"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAnalyze()
            }}
          />

          {error && (
            <Text color="negative" textStyle="bodySmall" textAlign="center" mb="12px">
              {error}
            </Text>
          )}

          <Button
            w="100%"
            h="52px"
            textStyle="body"
            fontWeight={500}
            borderRadius="10px"
            bg={!url ? 'bg.surface' : 'accent.500'}
            color={!url ? 'text.tertiary' : 'white'}
            cursor={!url ? 'not-allowed' : 'pointer'}
            border="1px solid"
            borderColor={!url ? 'border.subtle' : 'accent.500'}
            transition="all 0.3s ease"
            className={!url ? '' : 'animate-glow-pulse'}
            _hover={
              !url
                ? {}
                : {
                    bg: 'accent.400',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 0 30px rgba(0, 112, 243, 0.3)'
                  }
            }
            onClick={handleAnalyze}
          >
            Analyze
          </Button>
        </Box>
      </motion.div>
    </motion.div>
  )
}
